import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, Stack, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle as CheckIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { loadPracticeSummary } from '../../api/practiceApi';
import WorkspaceEngine from '../../engines/practice/WorkspaceEngine';

const PracticeEntry = ({ item, compact = false, onComplete }) => {
  const [open, setOpen] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const theme = useTheme();

  const refresh = useCallback(() => {
    loadPracticeSummary(item.sourceType, item.sourceKey)
      .then(setSummaries)
      .catch(() => setSummaries([]));
  }, [item.sourceKey, item.sourceType]);

  useEffect(refresh, [refresh]);

  const hasProgress = summaries.length > 0;
  const handleClose = () => { setOpen(false); refresh(); };

  // Best graded attempt across frameworks (SOLVE / 5-HOW) — one badge, not one
  // per framework, so this stays a single glance on the cohort page.
  const bestGrade = useMemo(() => {
    const graded = summaries.filter((s) => s.graded_at && s.max_score);
    if (!graded.length) return null;
    return graded.reduce((best, s) => (s.score / s.max_score > best.score / best.max_score ? s : best));
  }, [summaries]);

  return (
    <>
      <Box
        sx={{
          p: compact ? 2 : 3,
          borderRadius: 2,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: 0, right: 0,
          width: 120, height: 120,
          background: `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 65%)`,
          pointerEvents: 'none',
        }} />

        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={800} mb={0.5}>
          {hasProgress ? 'Continue optional practice' : 'Optional practice'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1.5 : 2.5, maxWidth: 500 }}>
          {hasProgress ? (
            'Resume where you left off. Use this only to rehearse the idea; your real project evidence stays in GitHub.'
          ) : (
            'Use this only when you need extra thinking practice. Do not copy your daily task here.'
          )}
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1}>
          <Button
            variant="contained"
            size={compact ? 'small' : 'large'}
            startIcon={hasProgress ? <CheckIcon /> : <PlayIcon />}
            onClick={() => setOpen(true)}
            sx={{ minWidth: compact ? 140 : 200, fontWeight: 700 }}
          >
            {hasProgress ? 'Resume SOLVE / 5-HOW' : 'Open SOLVE / 5-HOW'}
          </Button>
          {hasProgress && (
            <Typography variant="caption" color="success.main" fontWeight={700}>
              ✓ Progress saved
            </Typography>
          )}
          {bestGrade && (
            <Chip
              size="small"
              color={bestGrade.score / bestGrade.max_score >= 0.7 ? 'success' : 'warning'}
              label={`Practice score: ${bestGrade.score}/${bestGrade.max_score}`}
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'background.default' } }}
      >
        <WorkspaceEngine item={item} onClose={handleClose} onComplete={onComplete} />
      </Dialog>
    </>
  );
};

export default PracticeEntry;
